import classnames from 'classnames'
import { Component } from 'preact'
import { z } from 'zod'
import Phrase from '../../libs/translationsUtils/phrase'
import Card from '../cards/index'
import { CategoryInfo, ParentCategoryInfo } from '../mega-menu/Types'
import { textToKebab } from '../utils/toKebabCase'
import WidgetContainer, { widgetContainerConfig } from '../widget-container'

interface CommunityCategoryWidgetProps {
  config: CommunityCategoryConfig
  categories: ParentCategoryInfo[]
}

export const widgetName = 'community_category' as const

export const config = z
  .object({
    selectedTemplate: z.number(),
    displayOnlyParent: z.boolean()
  })
  .merge(widgetContainerConfig)

export type CommunityCategoryConfig = z.infer<typeof config>

class CommunityCategoryWrapper extends Component<CommunityCategoryWidgetProps> {
  constructor(props: CommunityCategoryWidgetProps) {
    super(props)
    this.getMetadata = this.getMetadata.bind(this)
  }

  templates = ['big-card', 'small-card']

  getMetadata(categories: CategoryInfo[]) {
    let totalTopicCount = 0
    let totalReplyCount = 0

    categories.forEach((category) => {
      totalTopicCount += category.topicCount
      totalReplyCount += category.replyCount
    })

    return (
      <ul className='list--dot-seperated community-category__card-metadata'>
        <li className='qa-forum-overview-topic-counter'>
          <Phrase m='Forum' p='forumList.meta.counter.topic' count={{ n: totalTopicCount.toString() }} />
        </li>
        <li className='qa-forum-overview-post-counter'>
          <Phrase m='Forum' p='forumList.meta.counter.post' count={{ n: totalReplyCount.toString() }} />
        </li>
      </ul>
    )
  }

  renderOnlyParent() {
    const { categories } = this.props
    return categories.map((item: ParentCategoryInfo, idx) => {
      const metadata = this.getMetadata(item.subforums)
      const kebabCasedTitle = textToKebab(item.title)
      return (
        <Card
          customClass={`community-category__card community-category__card-${
            this.templates[this.props.config.selectedTemplate]
          } id-${item.id} title-${kebabCasedTitle}`}
          idx={idx}
          key={item.id}
          title={item.title}
          metadata={metadata}
          imageSrc={item.thumbnailUrl}
          navigation={{
            url: item.url
          }}
        />
      )
    })
  }

  renderWithChilds() {
    return (
      <div className='community-category-list_parent-child-component-wrapper'>
        {this.props.categories.map((category) => (
          <section className='community-category-list_parent-child-component' key={category.id}>
            {this.props.config.title ? (
              <div className='community-category-list_parent-title'>{category.title}</div>
            ) : (
              <h3 className='widget__heading'>{category.title}</h3>
            )}
            <div className='community-category-list'>
              {category.subforums.map((subcategory, idx) => {
                const kebabCasedTitle = textToKebab(subcategory.title)
                return (
                  <Card
                    key={subcategory.id}
                    customClass={`community-category__card community-category__card-${
                      this.templates[this.props.config.selectedTemplate]
                    } id-${subcategory.id} title-${kebabCasedTitle}`}
                    idx={idx}
                    title={subcategory.title}
                    metadata={<Metadata topicCount={subcategory.topicCount} />}
                    imageSrc={subcategory.thumbnailUrl}
                    navigation={{
                      url: subcategory.url
                    }}
                  />
                )
              })}
            </div>
          </section>
        ))}
      </div>
    )
  }

  render() {
    const { config, categories } = this.props
    if (!categories.length) {
      return null
    }

    return (
      <WidgetContainer config={config} widgetName='community-category'>
        <div
          className={classnames(
            'full-width Template-brand-featured community-category-container qa-community-category-container custom-community-category'
          )}
        >
          <div className='community-category-list'>
            {config.displayOnlyParent ? this.renderOnlyParent() : this.renderWithChilds()}
          </div>
        </div>
      </WidgetContainer>
    )
  }
}

const Metadata = ({ topicCount }: { topicCount: number }) => (
  <div className='community__card-metadata'>
    <p className='text--meta'>
      <Phrase m='Forum' p='forumList.meta.counter.topic' count={{ n: topicCount.toString() }} />
    </p>
  </div>
)

export default CommunityCategoryWrapper
